/* ==========================================================================
   State & Storage Manager
   Handles user arsenal, session score entries, specs, and local persistence
   ========================================================================== */

const CURRENT_USER_KEY = 'bowling_companion_current_user_v1';
const BASE_STORAGE_KEY = 'bowling_companion_state_v2';

function getUserStorageKey(userName) {
  if (!userName || !userName.trim()) return BASE_STORAGE_KEY;
  const slug = userName.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '_');
  return `bowling_companion_user_${slug}`;
}

export const DEFAULT_PRESET_BALLS = [
  {
    id: 'p1',
    name: 'Origin EX',
    brand: '900 Global',
    coverType: 'Solid Reactive',
    coverName: 'RB 89 Solid',
    finish: '2000-grit Abralon',
    rg: 2.48,
    diff: 0.051,
    weight: 15,
    colors: ['#ff5722', '#b71c1c', '#4a148c'],
    gamesBowled: 42,
    lastDeoil: '2026-09-15',
    lastSanding: '2000 Abralon',
    lastDetoxGames: 22,
    lastResurfaceGames: 30,
    maintenanceLogs: [
      {
        id: 'm_p1_1',
        date: '2026-09-28',
        action: 'Resurface (Sanding)',
        grit: '2000-grit Abralon',
        gamesBowled: 30,
        notes: 'Full 500/1000/2000 resurface on spinner. Restored sharp backend motion.'
      },
      {
        id: 'm_p1_2',
        date: '2026-09-15',
        action: 'De-oil / Detox Bath',
        grit: '2000-grit Abralon',
        gamesBowled: 22,
        notes: 'Ultrasonic detox bath. Extracted lane oil build-up.'
      }
    ]
  },
  {
    id: 'p2',
    name: 'Ember Core',
    brand: '900 Global',
    coverType: 'Pearl Reactive',
    coverName: 'RB 82 Pearl',
    finish: '1500-grit Polish',
    rg: 2.50,
    diff: 0.048,
    weight: 15,
    colors: ['#ff4500', '#ff8c00', '#ffd700'],
    gamesBowled: 18,
    lastDeoil: '2026-09-01',
    lastSanding: '1500 Polish',
    lastDetoxGames: 6,
    lastResurfaceGames: 12,
    maintenanceLogs: [
      {
        id: 'm_p2_1',
        date: '2026-09-18',
        action: 'Resurface (Sanding)',
        grit: '1500-grit Polish',
        gamesBowled: 12,
        notes: 'Polished with Reacta Gloss on spinner for extra skid.'
      },
      {
        id: 'm_p2_2',
        date: '2026-09-01',
        action: 'De-oil / Detox Bath',
        grit: '1500-grit Polish',
        gamesBowled: 6,
        notes: 'Detox cycle completed.'
      }
    ]
  },
  {
    id: 'p3',
    name: 'Vengeance',
    brand: '900 Global',
    coverType: 'Solid Reactive',
    coverName: 'RB 91 Solid',
    finish: '2000-grit Abralon',
    rg: 2.47,
    diff: 0.055,
    weight: 15,
    colors: ['#222222', '#444444', '#777777'],
    gamesBowled: 26,
    lastDeoil: '2026-08-20',
    lastSanding: '2000 Abralon',
    lastDetoxGames: 10,
    lastResurfaceGames: 18,
    maintenanceLogs: [
      {
        id: 'm_p3_1',
        date: '2026-09-10',
        action: 'Resurface (Sanding)',
        grit: '2000-grit Abralon',
        gamesBowled: 18,
        notes: '2000 Abralon resurface.'
      },
      {
        id: 'm_p3_2',
        date: '2026-08-20',
        action: 'De-oil / Detox Bath',
        grit: '2000-grit Abralon',
        gamesBowled: 10,
        notes: 'Detox bath before tournament.'
      }
    ]
  },
  {
    id: 'p4',
    name: 'Pitch Black',
    brand: 'Storm',
    coverType: 'Urethane',
    coverName: 'Control Solid Urethane',
    finish: '1000-grit Abralon',
    rg: 2.57,
    diff: 0.022,
    weight: 15,
    colors: ['#111111', '#333333', '#555555'],
    gamesBowled: 35,
    lastDeoil: 'N/A (Urethane)',
    lastSanding: '1000 Abralon',
    lastDetoxGames: 0,
    lastResurfaceGames: 25,
    maintenanceLogs: [
      {
        id: 'm_p4_1',
        date: '2026-09-22',
        action: 'Resurface (Sanding)',
        grit: '1000-grit Abralon',
        gamesBowled: 25,
        notes: 'Fresh 1000 grit scuff for heavy oil short patterns.'
      }
    ]
  }
];

export const DEFAULT_STATE = {
  currentUser: '',
  cloudStatus: 'local', // 'local' | 'syncing' | 'synced' | 'offline' | 'error'
  lastSyncedAt: null,
  activeTab: 'tab-home',
  activeBallId: 'p1',
  activeOilCondition: 'fresh',
  standBoard: 20,
  targetBoard: 10,
  speed: 16.5,
  revRate: 350,
  drilling: 'benchmark',
  compareMode: false,
  compareBallId: 'p2',
  leagueId: '18776',
  leagueName: 'Lockheed Martin Monogram',
  bowlingCenter: 'Flamingo Bowl',
  lanes: 'Lanes 17-18',
  leagueBowlers: [],
  leagueStandings: [],
  session: {
    opponent: 'HAZARDS',
    game1: 205,
    game2: 224,
    game3: 218,
    ballGame1: 'p1',
    ballGame2: 'p4',
    ballGame3: 'p2',
    pointsWon: '6.0 / 7.0'
  },
  history: [
    {
      id: 'h1',
      date: '2026-09-18',
      opponent: 'Miswired',
      game1: 214,
      game2: 198,
      game3: 210,
      ballGame1: 'p1',
      ballGame2: 'p2',
      ballGame3: 'p1',
      seriesTotal: 622,
      seriesAvg: 207.3,
      pointsWon: '5.0 / 7.0',
      notes: 'Flamingo Pair 17-18: Right lane breaking down faster. Move 2 boards left.'
    },
    {
      id: 'h2',
      date: '2026-09-25',
      opponent: 'HARCLAY GEMS',
      game1: 235,
      game2: 212,
      game3: 245,
      ballGame1: 'p1',
      ballGame2: 'p3',
      ballGame3: 'p2',
      seriesTotal: 692,
      seriesAvg: 230.7,
      pointsWon: '7.0 / 7.0',
      notes: 'High series 692! Stood 22 board, 10-pin carry clean.'
    },
    {
      id: 'h3',
      date: '2026-10-02',
      opponent: 'HAZARDS',
      game1: 205,
      game2: 224,
      game3: 218,
      ballGame1: 'p1',
      ballGame2: 'p4',
      ballGame3: 'p2',
      seriesTotal: 647,
      seriesAvg: 215.7,
      pointsWon: '6.0 / 7.0',
      notes: 'Urethane Pitch Black pulled through in Game 2 when front oil burned.'
    }
  ],
  notes: [
    { id: 'n1', context: 'Game 1 • Frame 4', text: 'Flamingo Bowl Pair 17-18: Right lane breaking down faster. Move 2 boards left.' },
    { id: 'n2', context: 'Game 1 • Frame 8', text: '10-pin carry is clean when hitting board 10 at 40 feet.' }
  ],
  specs: {
    papHoriz: '5.0" Right',
    papVert: '0.5" Up',
    drillAngle: '45°',
    pinToPap: '4.5"',
    valAngle: '35°',
    spanMiddle: '4 3/8"',
    spanRing: '4 7/16"',
    thumbPitch: '1/8" Right, 1/4" Reverse'
  },
  arsenal: DEFAULT_PRESET_BALLS
};

class StateManager {
  constructor() {
    this.currentUser = localStorage.getItem(CURRENT_USER_KEY) || '';
    this.cloudStatus = this.currentUser ? 'syncing' : 'local';
    this.lastSyncedAt = null;
    this.data = this.loadState();
    this.listeners = [];
    this.syncTimeout = null;
    this.syncHooks = [];

    // Trigger cloud sync if logged in
    if (this.currentUser) {
      setTimeout(() => this.syncWithCloud(this.currentUser), 300);
    }

    // Always trigger registered live sync hooks (including League Secretary) on initial load
    setTimeout(() => this.triggerSyncHooks(), 600);
  }

  loadState() {
    const key = getUserStorageKey(this.currentUser);
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.arsenal)) {
          parsed.arsenal = parsed.arsenal.map(b => {
            const presetMatch = DEFAULT_PRESET_BALLS.find(p => p.id === b.id);
            const logs = Array.isArray(b.maintenanceLogs) && b.maintenanceLogs.length > 0
              ? b.maintenanceLogs
              : (presetMatch?.maintenanceLogs || []);
            const detoxGames = Number.isFinite(b.lastDetoxGames)
              ? b.lastDetoxGames
              : (presetMatch?.lastDetoxGames ?? 0);
            const resurfaceGames = Number.isFinite(b.lastResurfaceGames)
              ? b.lastResurfaceGames
              : (presetMatch?.lastResurfaceGames ?? 0);

            return {
              ...b,
              maintenanceLogs: logs,
              lastDetoxGames: detoxGames,
              lastResurfaceGames: resurfaceGames
            };
          });
        }
        return {
          ...DEFAULT_STATE,
          ...parsed,
          currentUser: this.currentUser,
          cloudStatus: this.cloudStatus,
          lastSyncedAt: this.lastSyncedAt
        };
      }
    } catch (e) {
      console.warn('Could not read localStorage:', e);
    }
    return {
      ...DEFAULT_STATE,
      currentUser: this.currentUser,
      cloudStatus: this.cloudStatus,
      lastSyncedAt: this.lastSyncedAt
    };
  }

  saveState() {
    const key = getUserStorageKey(this.currentUser);
    try {
      localStorage.setItem(key, JSON.stringify(this.data));
    } catch (e) {
      console.warn('Could not save to localStorage:', e);
    }
    this.notify();

    if (this.currentUser) {
      if (this.syncTimeout) clearTimeout(this.syncTimeout);
      this.syncTimeout = setTimeout(() => {
        this.pushToCloud(this.currentUser);
      }, 1000);
    }
  }

  async setUser(userName) {
    const trimmed = (userName || '').trim();
    this.currentUser = trimmed;
    if (trimmed) {
      localStorage.setItem(CURRENT_USER_KEY, trimmed);
    } else {
      localStorage.removeItem(CURRENT_USER_KEY);
    }

    this.data = this.loadState();
    this.data.currentUser = trimmed;
    this.data.cloudStatus = trimmed ? 'syncing' : 'local';
    this.notify();

    if (trimmed) {
      await this.syncWithCloud(trimmed);
    }
  }

  async syncWithCloud(userName) {
    if (!userName || !userName.trim()) return;

    this.data.cloudStatus = 'syncing';
    this.notify();

    // Trigger registered live sync hooks (like League Secretary) in parallel
    this.triggerSyncHooks();

    try {
      const res = await fetch(`/api/sync?user=${encodeURIComponent(userName)}`);
      if (res.ok) {
        const json = await res.json();
        if (json.found && json.data && json.data.state) {
          this.data = {
            ...DEFAULT_STATE,
            ...json.data.state,
            currentUser: userName,
            cloudStatus: 'synced',
            lastSyncedAt: json.data.updatedAt || new Date().toISOString()
          };
          const key = getUserStorageKey(userName);
          localStorage.setItem(key, JSON.stringify(this.data));
          this.notify();
          return;
        }
      }

      if (res.status === 404) {
        await this.pushToCloud(userName);
        return;
      }

      if (res.status === 503) {
        this.data.cloudStatus = 'offline';
        this.notify();
        return;
      }

      this.data.cloudStatus = 'offline';
      this.notify();
    } catch (e) {
      console.warn('Cloud sync offline/error:', e);
      this.data.cloudStatus = 'offline';
      this.notify();
    }
  }

  registerSyncHook(fn) {
    if (typeof fn === 'function' && !this.syncHooks.includes(fn)) {
      this.syncHooks.push(fn);
    }
  }

  async triggerSyncHooks() {
    for (const hook of this.syncHooks) {
      try {
        await hook();
      } catch (e) {
        console.warn('Sync hook execution failed:', e);
      }
    }
  }

  async syncAll() {
    if (this.currentUser) {
      await this.syncWithCloud(this.currentUser);
    }
    await this.triggerSyncHooks();
  }

  async pushToCloud(userName) {
    if (!userName || !userName.trim()) return;

    try {
      const res = await fetch(`/api/sync?user=${encodeURIComponent(userName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.data)
      });

      if (res.ok) {
        const json = await res.json();
        this.data.cloudStatus = 'synced';
        this.data.lastSyncedAt = json.updatedAt || new Date().toISOString();
      } else if (res.status === 503) {
        this.data.cloudStatus = 'offline';
      } else {
        this.data.cloudStatus = 'error';
      }
    } catch (e) {
      console.warn('Cloud push offline/error:', e);
      this.data.cloudStatus = 'offline';
    }
    this.notify();
  }

  exportJson() {
    const jsonStr = JSON.stringify(this.data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bowling-data-${(this.currentUser || 'user').toLowerCase().replace(/[^a-z0-9]/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  importJson(jsonData) {
    try {
      const parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      this.data = {
        ...DEFAULT_STATE,
        ...parsed,
        currentUser: this.currentUser,
        cloudStatus: this.currentUser ? 'syncing' : 'local'
      };
      this.saveState();
      if (this.currentUser) {
        this.pushToCloud(this.currentUser);
      }
      return true;
    } catch (e) {
      console.error('Invalid JSON state import:', e);
      return false;
    }
  }

  get() {
    return this.data;
  }

  set(updates) {
    this.data = { ...this.data, ...updates };
    this.saveState();
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify() {
    this.listeners.forEach(l => l(this.data));
  }
}

export const appState = new StateManager();

